import { useEffect, useState } from 'react';

import type { AxisConfiguration } from 'components/molecules/PackArchitecture/axisConfiguration.utils';

const SHOW_TOP_MIN = 1;

type UseAxisShowTopInputParams = {
    totalCount: number;
    config: AxisConfiguration;
    onChange: (patch: Partial<AxisConfiguration>) => void;
};

const isShowTopOverMax = (value: number, totalCount: number) =>
    totalCount > 0 && Number.isFinite(value) && value > totalCount;

export const useAxisShowTopInput = ({ totalCount, config, onChange }: UseAxisShowTopInputParams) => {
    const [inputValue, setInputValue] = useState<number | null>(config.showTop);
    const [showTopInputError, setShowTopInputError] = useState(false);

    useEffect(() => {
        setInputValue(config.showTop);
        setShowTopInputError(false);
    }, [config.showTop]);

    const applyValidShowTop = (value: number) => {
        onChange({
            showTop: value,
            groupRestAsOther: value >= totalCount ? false : config.groupRestAsOther,
        });
        setInputValue(value);
        setShowTopInputError(false);
    };

    const handleShowTopInputChange = (rawValue: number | null) => {
        if (rawValue === null) {
            setInputValue(null);
            setShowTopInputError(true);
            return;
        }

        if (!Number.isFinite(rawValue)) {
            return;
        }

        setInputValue(rawValue);

        if (isShowTopOverMax(rawValue, totalCount)) {
            setShowTopInputError(true);
            return;
        }

        setShowTopInputError(false);

        if (rawValue >= SHOW_TOP_MIN) {
            applyValidShowTop(rawValue);
        }
    };

    const handleShowTopInputBlur = () => {
        const needsRevert =
            inputValue === null ||
            !Number.isFinite(inputValue) ||
            inputValue < SHOW_TOP_MIN ||
            isShowTopOverMax(inputValue, totalCount);

        if (needsRevert) {
            setInputValue(config.showTop);
            setShowTopInputError(false);
        }
    };

    const updateShowTopFromSlider = (showTop: number) => {
        applyValidShowTop(showTop);
    };

    return {
        showTopInputError,
        showTopInputValue: inputValue,
        handleShowTopInputChange,
        handleShowTopInputBlur,
        updateShowTopFromSlider,
        showTopMin: SHOW_TOP_MIN,
    };
};
